import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Calendar, Layers, Wallet } from 'lucide-react';

export const IssueCard = ({ issue = {}, coreRegistry = [] }) => {
  const name = issue.companyName || issue.company_name || issue.name || '';
  const symbol = issue.symbol || '';
  const series = issue.series || '';
  const status = issue.status || '';

  const isSme =
    series === 'SME' ||
    (issue.badge && issue.badge.toLowerCase().includes('sme')) ||
    (issue.category && issue.category.toLowerCase().includes('sme')) ||
    (issue.ipo_type && issue.ipo_type.toLowerCase().includes('sme')) ||
    (name && name.toLowerCase().includes('sme'));

  // Resolve ID for direct navigation to details page
  let targetId = issue.id;
  if (!targetId && coreRegistry && coreRegistry.length > 0) {
    const matched = coreRegistry.find(
      (c) =>
        c.company_name &&
        name &&
        (c.company_name.trim().toLowerCase() === name.trim().toLowerCase() ||
          c.company_name.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(c.company_name.toLowerCase()))
    );
    if (matched) {
      targetId = matched.id;
    }
  }

  const detailPath = targetId
    ? isSme
      ? `/smeipo/${targetId}`
      : `/mainboardipo/${targetId}`
    : isSme
      ? `/smeipo/${symbol || 'details'}`
      : `/mainboardipo/${symbol || 'details'}`;

  // Price Band / Issue Price
  const priceBand = issue.priceBand || issue.issuePrice || '';

  // Dates
  const openDate = issue.issueStartDate || issue.openDate || issue.drhp_date || '';
  const closeDate = issue.issueEndDate || issue.closeDate || '';
  const datesText =
    openDate && closeDate
      ? `${openDate} – ${closeDate}`
      : openDate || closeDate || 'To be announced';

  // Formatted Issue Size
  let issueSizeText = '';
  if (issue.issueSize) {
    const num = Number(issue.issueSize);
    if (!isNaN(num)) {
      if (num >= 10000000) {
        issueSizeText = `${(num / 10000000).toFixed(2)} Cr Shares`;
      } else if (num >= 100000) {
        issueSizeText = `${(num / 100000).toFixed(2)} L Shares`;
      } else {
        issueSizeText = `${num.toLocaleString('en-IN')} Shares`;
      }
    } else {
      issueSizeText = String(issue.issueSize);
    }
  } else if (issue.issue_size) {
    issueSizeText = issue.issue_size;
  }

  // Lot Size
  const lotSizeText = issue.lotSize
    ? `${Number(issue.lotSize).toLocaleString('en-IN')} Shares`
    : issue.lot_size
      ? `${issue.lot_size} Shares`
      : '—';

  // Calculated Min Investment
  let minInvestmentText = issue.minInvestment || '';
  if (!minInvestmentText && issue.lotSize && priceBand) {
    const matches = priceBand.match(/(\d+)/g);
    if (matches && matches.length > 0) {
      const highestPrice = Number(matches[matches.length - 1]);
      const lots = Number(issue.lotSize);
      if (!isNaN(highestPrice) && !isNaN(lots)) {
        minInvestmentText = `₹${(highestPrice * lots).toLocaleString('en-IN')}`;
      }
    }
  }

  const initialLetter = (name ? name.trim().charAt(0) : '•').toUpperCase();

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 hover:border-teal-400 p-4 sm:p-5 shadow-xs hover:shadow-lg transition-all duration-200 flex flex-col justify-between group h-full hover:-translate-y-1 select-none">
      <div>
        {/* Top Row: Avatar Box + Symbol on left, Status & Type Badges on right */}
        <div className="flex items-center justify-between gap-2 mb-2.5 min-h-[32px]">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-tr from-[#008080]/15 via-[#2dd4bf]/20 to-[#008080]/25 text-[#008080] font-extrabold text-xs sm:text-sm flex items-center justify-center border border-teal-200/80 shrink-0">
              {initialLetter}
            </div>
            {/* {symbol ? (
              <span className="aa text-[11px] sm:text-xs font-bold text-slate-500 bg-slate-50 border border-slate-200/80 px-2 py-0.5 rounded-lg truncate">
                {symbol}
              </span>
            ) : null} */}
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {status && (
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${status.toLowerCase() === 'active'
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  }`}
              >
                {status}
              </span>
            )}
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full border uppercase tracking-wider ${isSme
                ? 'bg-amber-50 text-amber-700 border-amber-200/80'
                : 'bg-teal-50 text-[#008080] border-teal-200/80'
                }`}
            >
              {isSme ? 'SME' : 'Mainboard'}
            </span>
          </div>
        </div>

        {/* Company Title (Dedicated Full-Width Line - Guaranteed Single Line) */}
        <Link to={detailPath} className="block group/title mt-1">
          <h3
            className="font-extrabold text-sm sm:text-[15px] lg:text-base text-slate-900 leading-tight group-hover/title:text-[#008080] transition-colors truncate"
            title={name}
          >
            {name}
          </h3>
        </Link>

        {/* Pricing Highlights Box */}
        <div className="mt-3.5 bg-slate-50/90 rounded-xl p-2.5 sm:p-3 border border-slate-100 grid grid-cols-2 gap-2 min-h-[58px]">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
              ISSUE PRICE
            </div>
            <div className="text-xs sm:text-sm font-extrabold text-[#008080] mt-0.5 truncate">
              {priceBand || 'Price on request'}
            </div>
          </div>

          <div className="text-right border-l border-slate-200/70 pl-2 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
              SERIES / TYPE
            </div>
            <div className="text-xs sm:text-sm font-extrabold text-slate-800 mt-0.5 truncate">
              {series === 'EQ' ? 'Equity (EQ)' : series || 'Book Built'}
            </div>
          </div>
        </div>

        {/* Financial Details Grid */}
        <div className="mt-3.5 space-y-2 text-xs">
          <div className="flex items-center justify-between text-slate-600 py-0.5 border-b border-slate-100">
            <span className="text-slate-500">Issue Size</span>
            <span className="font-bold text-slate-900">{issueSizeText || '—'}</span>
          </div>

          <div className="flex items-center justify-between text-slate-600 py-0.5 border-b border-slate-100">
            <span className="text-slate-500">Price Band</span>
            <span className="font-semibold text-slate-800">{priceBand || '—'}</span>
          </div>

          <div className="flex items-center justify-between text-slate-600 py-0.5 border-b border-slate-100">
            <span className="text-slate-500 flex items-center gap-1">
              <Calendar className="w-3 h-3 text-slate-400 shrink-0" />
              <span>Issue Dates</span>
            </span>
            <span className="text-slate-700 font-medium truncate max-w-[150px] text-right">
              {datesText}
            </span>
          </div>

          <div className="flex items-center justify-between text-slate-600 py-0.5 border-b border-slate-100">
            <span className="text-slate-500 flex items-center gap-1">
              <Wallet className="w-3 h-3 text-slate-400 shrink-0" />
              <span>Min Investment</span>
            </span>
            <span className="font-semibold text-slate-900">
              {minInvestmentText || '—'}
            </span>
          </div>

          <div className="flex items-center justify-between text-slate-600 py-0.5">
            <span className="text-slate-500 flex items-center gap-1">
              <Layers className="w-3 h-3 text-slate-400 shrink-0" />
              <span>Lot Size</span>
            </span>
            <span className="text-slate-700 font-medium">{lotSizeText}</span>
          </div>
        </div>
      </div>

      {/* Card Footer: Status Indicator + CTA Button */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
          <span className="font-semibold text-slate-700">NSE Live Feed</span>
        </div>

        <Link
          to={detailPath}
          className="inline-flex items-center gap-1 text-xs font-bold text-[#008080] hover:text-[#006666] group-hover:translate-x-0.5 transition-all"
        >
          <span>View Details</span>
          <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
};

export default IssueCard;
